import { useEffect, useRef } from 'react';

export function FeedbackDownloadDialog({ download, onDownload, onClose }) {
  const downloadRef = useRef(null);
  const dialogRef = useRef(null);
  const backdropRef = useRef(null);
  const downloadUrl = download?.url;
  const returnFocus = download?.returnFocus;

  useEffect(() => {
    if (!downloadUrl) return undefined;
    const previousFocus = returnFocus || document.activeElement;
    const backdrop = backdropRef.current;
    const background = backdrop?.parentElement
      ? [...backdrop.parentElement.children].filter((element) => element !== backdrop)
      : [];
    const backgroundState = background.map((element) => ({
      element,
      inert: element.hasAttribute('inert'),
      ariaHidden: element.getAttribute('aria-hidden'),
    }));
    for (const element of background) {
      element.setAttribute('inert', '');
      element.setAttribute('aria-hidden', 'true');
    }
    downloadRef.current?.focus();
    return () => {
      for (const { element, inert, ariaHidden } of backgroundState) {
        if (!inert) element.removeAttribute('inert');
        if (ariaHidden == null) element.removeAttribute('aria-hidden');
        else element.setAttribute('aria-hidden', ariaHidden);
      }
      if (previousFocus?.isConnected && typeof previousFocus.focus === 'function') {
        previousFocus.focus();
      }
    };
  }, [downloadUrl, returnFocus]);

  if (!download) return null;

  const started = Boolean(download.started);
  const finalized = download.finalStatus === 'final';
  const title = started ? 'Feedback ZIP download requested' : 'Feedback ZIP ready';

  return (
    <div ref={backdropRef} className="feedback-download-backdrop">
      <section
        ref={dialogRef}
        className="feedback-download-dialog"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onKeyDown={(event) => {
          if (event.key === 'Escape') {
            event.preventDefault();
            event.stopPropagation();
            onClose();
            return;
          }
          if (event.key !== 'Tab') return;
          const focusable = [
            ...dialogRef.current.querySelectorAll(
              'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
            ),
          ];
          if (!focusable.length) {
            event.preventDefault();
            dialogRef.current.focus();
            return;
          }
          const first = focusable[0];
          const last = focusable[focusable.length - 1];
          if (event.shiftKey && document.activeElement === first) {
            event.preventDefault();
            last.focus();
          } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first.focus();
          }
        }}
      >
        <div className="feedback-download-dialog-icon" aria-hidden>
          ↓
        </div>
        <div className="feedback-download-dialog-copy">
          <p className="feedback-download-dialog-kicker">Developer-ready handoff</p>
          <h2>{started ? 'Download requested' : 'Your ZIP is ready'}</h2>
          <p>
            {started
              ? 'Your browser was asked to save the file. Confirm it appears in your downloads before relying on it as evidence.'
              : finalized
                ? 'This feedback package is finalized. Select Download ZIP to save the prepared handoff file.'
                : 'Select Download ZIP to hand the prepared file to your browser. Your feedback remains a draft.'}
          </p>
          <code>{download.filename}</code>
        </div>
        <div className="feedback-download-dialog-actions">
          <button type="button" className="btn ghost" onClick={onClose}>
            {started || finalized ? 'Close' : 'Cancel'}
          </button>
          <a
            ref={downloadRef}
            className="btn primary"
            href={download.url}
            download={download.filename}
            onClick={onDownload}
          >
            {started ? 'Download again' : 'Download ZIP'}
          </a>
        </div>
      </section>
    </div>
  );
}
